import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import {
  Bell, Check, Clock, Mail, MailX, Smartphone, Search,
  Users as UsersIcon, Briefcase, DollarSign,
} from "lucide-react";
import AdminJobViewModal from "@/components/AdminJobViewModal";
import AdvancedEditJobModal from "@/components/AdvancedEditJobModal";
import UserProfileModal from "@/components/UserProfileModal";
import { useTheme } from "@/contexts/ThemeContext";

import DashboardMetricCards from "@/components/admin-dashboard/DashboardMetricCards";
import DashboardActionItems from "@/components/admin-dashboard/DashboardActionItems";
import DashboardRevenueChart from "@/components/admin-dashboard/DashboardRevenueChart";
import DashboardJobsChart from "@/components/admin-dashboard/DashboardJobsChart";
import DashboardGeographicChart from "@/components/admin-dashboard/DashboardGeographicChart";
import DashboardPlatformHealth from "@/components/admin-dashboard/DashboardPlatformHealth";
import DashboardPerformance from "@/components/admin-dashboard/DashboardPerformance";
import PushNotificationModals from "@/components/admin-dashboard/PushNotificationModals";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const { isDark } = useTheme();

  // Phase 1 data (quick endpoints)
  const [stats, setStats] = useState(null);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [pendingJobs, setPendingJobs] = useState([]);
  const [releasePaymentJobs, setReleasePaymentJobs] = useState([]);
  const [paymentSummary, setPaymentSummary] = useState(null);
  const [pushRegCount, setPushRegCount] = useState(0);
  const [bounceStats, setBounceStats] = useState(null);
  const [loadingPhase1, setLoadingPhase1] = useState(true);

  // Phase 2 data (reports — may be slow)
  const [reports, setReports] = useState(null);
  const [loadingReports, setLoadingReports] = useState(false);
  const [reportsLoaded, setReportsLoaded] = useState(false);

  // Modal states
  const [selectedJob, setSelectedJob] = useState(null);
  const [showJobModal, setShowJobModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [jobToApprove, setJobToApprove] = useState(null);
  const [eligibleUsersCount, setEligibleUsersCount] = useState(0);
  const [pushUsersCount, setPushUsersCount] = useState(0);
  const [loadingCount, setLoadingCount] = useState(false);

  // Push notification modal states
  const [showPushModal, setShowPushModal] = useState(false);
  const [pushRegUsers, setPushRegUsers] = useState([]);
  const [pushRegPage, setPushRegPage] = useState(1);
  const [pushRegTotalPages, setPushRegTotalPages] = useState(1);
  const [pushRegSearch, setPushRegSearch] = useState("");
  const [loadingPushReg, setLoadingPushReg] = useState(false);
  const [showSendPushModal, setShowSendPushModal] = useState(false);
  const [sendPushUser, setSendPushUser] = useState(null);
  const [pushTitle, setPushTitle] = useState("");
  const [pushMessage, setPushMessage] = useState("");
  const [pushLinkUrl, setPushLinkUrl] = useState("");
  const [sendingPush, setSendingPush] = useState(false);

  // Platform-wide search (local filter over already-fetched data — no new backend call)
  const [search, setSearch] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);
  const searchRef = useRef(null);

  // Phase 1: Quick endpoints (instant)
  const fetchPhase1 = useCallback(async () => {
    setLoadingPhase1(true);
    const results = await Promise.allSettled([
      axios.get(`${BACKEND}/api/admin/stats`),
      axios.get(`${BACKEND}/api/admin/pending-approvals`),
      axios.get(`${BACKEND}/api/admin/jobs/pending-approval`),
      axios.get(`${BACKEND}/api/admin/jobs/release-payment`),
      axios.get(`${BACKEND}/api/admin/payments/summary`),
      axios.get(`${BACKEND}/api/admin/push-registrations/count`),
      axios.get(`${BACKEND}/api/admin/bounce-statistics`),
    ]);

    const get = (i) => results[i]?.status === "fulfilled" ? results[i].value.data : null;

    const statsData = get(0);
    if (statsData) {
      setStats({
        totalUsers: statsData.total_users || 0,
        pendingUsers: statsData.pending_users || 0,
        approvedUsers: statsData.approved_users || 0,
        totalJobs: statsData.total_jobs || 0,
        activeJobs: statsData.active_jobs || 0,
        completedJobs: statsData.completed_jobs || 0,
        pendingJobs: statsData.pending_jobs || 0,
      });
    }
    setPendingApprovals(get(1)?.pending_users || []);
    setPendingJobs(get(2)?.jobs || []);
    setReleasePaymentJobs(get(3)?.jobs || []);
    setPaymentSummary(get(4)?.summary || null);
    setPushRegCount(get(5)?.count || 0);
    setBounceStats(get(6) || null);
    setLoadingPhase1(false);
  }, []);

  // Phase 2: Reports endpoint (deferred - loads after phase 1)
  const fetchReports = useCallback(async () => {
    if (reportsLoaded) return;
    setLoadingReports(true);
    try {
      const response = await axios.get(`${BACKEND}/api/admin/reports/dashboard`);
      setReports(response.data);
      setReportsLoaded(true);
    } catch (err) {
      console.error("Failed to fetch reports:", err);
    } finally {
      setLoadingReports(false);
    }
  }, [reportsLoaded]);

  useEffect(() => {
    fetchPhase1();
  }, [fetchPhase1]);

  // Defer reports loading - fetch after phase 1 completes
  useEffect(() => {
    if (!loadingPhase1 && !reportsLoaded) {
      fetchReports();
    }
  }, [loadingPhase1, reportsLoaded, fetchReports]);

  // Push registration helpers
  const fetchPushRegUsers = async (page = 1, search = "") => {
    setLoadingPushReg(true);
    try {
      const response = await axios.get(`${BACKEND}/api/admin/push-registrations`, { params: { page, limit: 10, search } });
      setPushRegUsers(response.data.users || []);
      setPushRegTotalPages(response.data.total_pages || 1);
      setPushRegPage(page);
    } catch (err) {
      console.error("Failed to fetch push registrations:", err);
    } finally {
      setLoadingPushReg(false);
    }
  };

  const handleSendPush = async () => {
    if (!sendPushUser || !pushTitle.trim() || !pushMessage.trim()) return;
    try {
      setSendingPush(true);
      await axios.post(`${BACKEND}/api/admin/push-registrations/send`, {
        user_email: sendPushUser.email,
        title: pushTitle.trim(),
        message: pushMessage.trim(),
        ...(pushLinkUrl.trim() ? { link_url: pushLinkUrl.trim() } : {}),
      });
      setShowSendPushModal(false);
      setSendPushUser(null);
      setPushTitle("");
      setPushMessage("");
      setPushLinkUrl("");
      alert(`Push notification sent to ${sendPushUser.full_name || sendPushUser.email}!`);
    } catch (err) {
      alert("Failed to send push notification: " + (err.response?.data?.detail || err.message));
    } finally {
      setSendingPush(false);
    }
  };

  // Action handlers
  const handleApproveUser = async (userId) => {
    try {
      await axios.put(`${BACKEND}/api/admin/users/${userId}/approval`, { approval_status: "approved" });
      fetchPhase1();
    } catch (err) {
      console.error("Failed to approve user:", err);
    }
  };

  const handleApproveJob = async (job) => {
    setJobToApprove(job);
    setShowApprovalModal(true);
    setLoadingCount(true);
    try {
      const response = await axios.get(`${BACKEND}/api/admin/count-job-notification-recipients/${job.id}`);
      setEligibleUsersCount(response.data.email_count || response.data.count || 0);
      setPushUsersCount(response.data.push_count || 0);
    } catch {
      setEligibleUsersCount(0);
      setPushUsersCount(0);
    } finally {
      setLoadingCount(false);
    }
  };

  const handleApprovalDecision = async (jobId, shouldNotify = true) => {
    try {
      await axios.post(`${BACKEND}/api/admin/jobs/${jobId}/approve`, {
        notify_users: shouldNotify,
        admin_email: "hello@shootzu.com",
      });
      setShowApprovalModal(false);
      setJobToApprove(null);
      setShowJobModal(false);
      setSelectedJob(null);
      fetchPhase1();
    } catch (err) {
      console.error("Error approving job:", err);
    }
  };

  const handleCancelJob = async (jobId, reason = "") => {
    try {
      await axios.post(`${BACKEND}/api/admin/jobs/${jobId}/cancel`, { reason });
      fetchPhase1();
    } catch (err) {
      console.error("Error cancelling job:", err);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return "$0.00";
    return `$${parseFloat(amount).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const getJobTypeLabel = (type) => {
    const map = { WEDDINGS: "Wedding", CORPORATE: "Corporate", EVENTS: "Events", REAL_ESTATE: "Real Estate", OTHERS: "Other" };
    return map[type] || type || "";
  };

  const todayDate = new Date().toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  // ⌘K / Ctrl+K focuses the platform search
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Grouped results filtered locally from already-fetched data (no new fetch)
  const searchResults = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return [];
    const out = [];
    (pendingApprovals || []).forEach((u) => {
      const hay = `${u.full_name || ""} ${u.email || ""} ${u.role || ""}`.toLowerCase();
      if (hay.includes(q)) {
        out.push({
          key: `user-${u.id}`, type: "User", label: u.full_name || u.email,
          sub: [u.email, u.role].filter(Boolean).join(" · "),
          chipBg: "#DBEAFE", chipFg: "#2563EB", Icon: UsersIcon,
          onClick: () => { setSelectedUser(u); setShowUserModal(true); setSearch(""); setSearchFocused(false); },
        });
      }
    });
    (pendingJobs || []).forEach((j) => {
      const hay = `${j.title || ""} ${j.location || ""} ${j.owner?.name || j.owner_name || ""}`.toLowerCase();
      if (hay.includes(q)) {
        out.push({
          key: `job-${j.id}`, type: "Job", label: j.title,
          sub: [j.job_number, j.owner?.name || j.owner_name, j.location || j.location_address].filter(Boolean).join(" · "),
          chipBg: "#EDE4F4", chipFg: "#78287C", Icon: Briefcase,
          onClick: () => { setSelectedJob(j); setShowJobModal(true); setSearch(""); setSearchFocused(false); },
        });
      }
    });
    (releasePaymentJobs || []).forEach((p) => {
      const hay = `${p.title || ""} ${p.crew_name || ""}`.toLowerCase();
      if (hay.includes(q)) {
        out.push({
          key: `pay-${p.id}`, type: "Payment", label: p.title,
          sub: [p.crew_name, formatCurrency(p.total_crew_payout || p.budget)].filter(Boolean).join(" · "),
          chipBg: "#DCFCE7", chipFg: "#15803D", Icon: DollarSign,
          onClick: () => { setSelectedJob(p); setShowJobModal(true); setSearch(""); setSearchFocused(false); },
        });
      }
    });
    return out;
  }, [search, pendingApprovals, pendingJobs, releasePaymentJobs]);

  const searchOpen = searchFocused && search.trim().length > 0;

  // Compose data object for metric cards
  const metricData = {
    stats,
    reports,
    pendingApprovals,
    pendingJobs,
    releasePaymentJobs,
    paymentSummary,
    pushRegCount,
  };

  return (
    <div>
      {/* Sticky Page Header */}
      <header className="sz-header flex items-center justify-between gap-[18px]">
        <h1 className="sz-h1 m-0 whitespace-nowrap">Dashboard</h1>

        {/* Platform-wide search */}
        <div className="relative hidden flex-1 md:block" style={{ maxWidth: 480 }}>
          <Search className="pointer-events-none absolute left-3.5 top-1/2 h-[17px] w-[17px] -translate-y-1/2 text-[#A8AEBC]" strokeWidth={2} />
          <input
            ref={searchRef}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setTimeout(() => setSearchFocused(false), 150)}
            placeholder="Search users, jobs, payments…"
            className="sz-input !rounded-xl !py-[11px] !pl-10 !pr-12 !text-[13.5px]"
          />
          <span
            className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 rounded-md border border-[#E6E9EF] px-1.5 py-0.5 text-[11px] font-semibold text-[#A8AEBC]"
            style={{ fontFamily: "Poppins,sans-serif" }}
          >
            ⌘K
          </span>
          {searchOpen && (
            <div
              className="szscroll sz-fade absolute left-0 right-0 top-[48px] z-40 overflow-y-auto rounded-2xl border border-[#E6E9EF] bg-white p-[7px]"
              style={{ maxHeight: 380, boxShadow: "0 18px 44px -14px rgba(12,17,32,.3)" }}
            >
              <div className="px-2.5 pb-1.5 pt-2 text-[10.5px] font-bold uppercase tracking-[.4px] text-[#A8AEBC]" style={{ fontFamily: "Poppins,sans-serif" }}>
                {searchResults.length} result{searchResults.length === 1 ? "" : "s"}
              </div>
              {searchResults.length === 0 ? (
                <div className="px-2.5 py-6 text-center text-[12.5px] text-[#8A90A2]">No matches for “{search}”.</div>
              ) : (
                searchResults.map((r) => {
                  const RIcon = r.Icon;
                  return (
                    <button
                      key={r.key}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={r.onClick}
                      className="flex w-full items-center gap-[11px] rounded-[10px] px-2.5 py-[9px] text-left transition-colors hover:bg-[#F5F6F9]"
                    >
                      <div className="flex h-8 w-8 flex-none items-center justify-center rounded-[9px]" style={{ background: r.chipBg, color: r.chipFg }}>
                        <RIcon className="h-4 w-4" strokeWidth={2} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-[13px] font-semibold text-[#0C1120]">{r.label}</div>
                        <div className="truncate text-[11.5px] text-[#8A90A2]">{r.sub}</div>
                      </div>
                      <span
                        className="flex-none rounded-full px-2 py-[3px] text-[10px] font-bold"
                        style={{ fontFamily: "Poppins,sans-serif", color: r.chipFg, background: r.chipBg }}
                      >
                        {r.type}
                      </span>
                    </button>
                  );
                })
              )}
            </div>
          )}
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => { setShowPushModal(true); fetchPushRegUsers(1, ""); }}
            className="relative flex items-center gap-[9px] rounded-[11px] border border-[#E0E3EB] bg-white py-[9px] pl-3 pr-[15px] text-[13px] font-semibold text-[#2B3142] transition-colors hover:border-[#28C0D0]"
            title="Push Registrations"
          >
            <Bell className="h-[17px] w-[17px] text-[#572B7C]" strokeWidth={2} />
            <span className="hidden sm:inline">Push Registrations</span>
            <span
              className="inline-flex h-[21px] min-w-[21px] items-center justify-center rounded-full px-1.5 text-[11px] font-bold text-white"
              style={{ fontFamily: "Poppins,sans-serif", background: "linear-gradient(120deg,#572B7C,#78287C)" }}
            >
              {pushRegCount}
            </span>
          </button>
          <div className="hidden items-center gap-[9px] rounded-full border border-[#E6E9EF] bg-white py-[5px] pl-[5px] pr-3.5 sm:flex">
            <div
              className="flex h-[30px] w-[30px] items-center justify-center rounded-full text-[12px] font-bold text-white"
              style={{ fontFamily: "Poppins,sans-serif", background: "linear-gradient(135deg,#572B7C,#28C0D0)" }}
            >
              SZ
            </div>
            <span className="text-[12.5px] font-semibold text-[#2B3142]">Admin</span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main style={{ padding: "22px 30px 56px", maxWidth: 1320 }}>
        {/* Metric Cards */}
        <DashboardMetricCards data={metricData} loading={loadingPhase1} />

        {/* Action Items */}
        <DashboardActionItems
          pendingApprovals={pendingApprovals}
          pendingJobs={pendingJobs}
          releasePaymentJobs={releasePaymentJobs}
          loading={loadingPhase1}
          onApproveUser={handleApproveUser}
          onViewJob={(job) => { setSelectedJob(job); setShowJobModal(true); }}
          onEditJob={(job) => { setSelectedJob(job); setShowEditModal(true); }}
          onApproveJob={handleApproveJob}
          onCancelJob={handleCancelJob}
          onViewUser={(user) => { setSelectedUser(user); setShowUserModal(true); }}
        />

        {/* Charts Row 1 */}
        <div className="mt-[18px] grid grid-cols-1 gap-[18px] lg:grid-cols-[1.55fr_1fr]">
          <DashboardRevenueChart
            monthlyRevenue={reports?.financial?.monthly_revenue}
            maxMonthly={reports?.financial?.max_monthly}
            loading={loadingReports}
            isDark={isDark}
          />
          <DashboardJobsChart
            jobsByStatus={reports?.jobs?.by_status}
            totalJobs={reports?.jobs?.total_jobs}
            loading={loadingReports}
            isDark={isDark}
          />
        </div>

        {/* Charts Row 2 */}
        <div className="mt-[18px] grid grid-cols-1 gap-[18px] lg:grid-cols-2">
          <DashboardGeographicChart
            jobsByState={reports?.geographic?.jobs_by_state}
            loading={loadingReports}
            isDark={isDark}
          />
          <DashboardPlatformHealth
            performance={reports?.performance}
            bounceStats={bounceStats}
            pushRegCount={pushRegCount}
            loading={loadingReports}
            onPushClick={() => { setShowPushModal(true); fetchPushRegUsers(1, ""); }}
          />
        </div>

        {/* Leaderboards */}
        <DashboardPerformance performance={reports?.performance} loading={loadingReports} />
      </main>

      {/* Modals */}
      <AdminJobViewModal
        job={selectedJob}
        isOpen={showJobModal}
        onClose={() => { setShowJobModal(false); setSelectedJob(null); }}
        onApprove={(job) => { setShowJobModal(false); handleApproveJob(job); }}
        onEdit={(job) => { setShowJobModal(false); setSelectedJob(job); setShowEditModal(true); }}
        onCancel={(jobId, reason) => { handleCancelJob(jobId, reason); setShowJobModal(false); setSelectedJob(null); }}
      />

      {showEditModal && selectedJob && (
        <AdvancedEditJobModal
          job={selectedJob}
          isOpen={showEditModal}
          onClose={() => { setShowEditModal(false); setSelectedJob(null); }}
          onUpdate={() => { fetchPhase1(); setShowEditModal(false); setSelectedJob(null); }}
        />
      )}

      {showUserModal && selectedUser && (
        <UserProfileModal
          user={selectedUser}
          isOpen={showUserModal}
          onClose={() => { setShowUserModal(false); setSelectedUser(null); }}
          onApprove={() => { handleApproveUser(selectedUser.id); setShowUserModal(false); setSelectedUser(null); }}
        />
      )}

      {/* Approval Decision Modal */}
      {showApprovalModal && jobToApprove && (
        <div
          className="sz-modal-backdrop"
          style={{ zIndex: 60 }}
          onClick={(e) => { if (e.target === e.currentTarget) { setShowApprovalModal(false); setJobToApprove(null); } }}
        >
          <div className="sz-modal-card w-full overflow-hidden" style={{ maxWidth: 430 }} onClick={(e) => e.stopPropagation()}>
            {/* Gradient header */}
            <div className="sz-modal-header-grad px-6 py-[22px]">
              <div className="flex items-center gap-[11px]">
                <div className="flex h-[38px] w-[38px] flex-none items-center justify-center rounded-[11px]" style={{ background: "rgba(255,255,255,.18)" }}>
                  <Check className="h-5 w-5 text-white" strokeWidth={2.2} />
                </div>
                <div className="min-w-0">
                  <h3 className="truncate text-[17px] font-bold text-white" style={{ fontFamily: "Poppins,sans-serif" }}>
                    Approve “{jobToApprove.title}”?
                  </h3>
                  <div className="text-[12.5px]" style={{ color: "rgba(255,255,255,.78)" }}>Job pending approval</div>
                </div>
              </div>
            </div>

            <div className="szscroll overflow-y-auto px-5 py-[18px]">
              <div className="mb-3 rounded-xl bg-[#F5F6F9] p-3 text-[12.5px] text-[#2B3142]">
                <p><span className="font-semibold">Budget:</span> {formatCurrency(jobToApprove.budget || jobToApprove.total_amount)}</p>
                {jobToApprove.job_type && <p className="mt-1"><span className="font-semibold">Type:</span> {getJobTypeLabel(jobToApprove.job_type)}</p>}
                {(jobToApprove.owner?.name || jobToApprove.owner_name) && (
                  <p className="mt-1"><span className="font-semibold">Posted by:</span> {jobToApprove.owner?.name || jobToApprove.owner_name}</p>
                )}
              </div>

              <div className="mb-3 space-y-2">
                <div className="rounded-xl border border-[#DBEAFE] bg-[#EFF6FF] p-3 text-center text-[12.5px] text-[#2563EB]">
                  {loadingCount ? (
                    <span className="inline-flex items-center gap-2"><Clock className="h-4 w-4 animate-spin" /> Counting eligible users…</span>
                  ) : (
                    <span className="inline-flex items-center gap-2 font-semibold">
                      <Mail className="h-4 w-4" /> {eligibleUsersCount} crew within 300km will receive email
                    </span>
                  )}
                </div>
                <div className="rounded-xl border border-[#EDE4F4] bg-[#FAF7FC] p-3 text-center text-[12.5px] text-[#78287C]">
                  {loadingCount ? (
                    <span className="inline-flex items-center gap-2"><Clock className="h-4 w-4 animate-spin" /> …</span>
                  ) : (
                    <span className="inline-flex items-center gap-2 font-semibold">
                      <Smartphone className="h-4 w-4" /> {pushUsersCount} crew will receive push notification
                    </span>
                  )}
                </div>
              </div>

              <button
                onClick={() => handleApprovalDecision(jobToApprove.id, true)}
                className="mb-[9px] flex w-full items-center gap-[11px] rounded-xl bg-[#0C1120] px-[15px] py-[13px] text-left text-white transition-colors hover:bg-[#28C0D0] hover:text-[#0C1120]"
              >
                <Bell className="h-[19px] w-[19px]" strokeWidth={2} />
                <div>
                  <div className="text-[13.5px] font-semibold" style={{ fontFamily: "Poppins,sans-serif" }}>Approve &amp; Notify {eligibleUsersCount} User{eligibleUsersCount !== 1 ? "s" : ""}</div>
                  <div className="text-[11.5px] opacity-75">Send approval email + push</div>
                </div>
              </button>
              <button
                onClick={() => handleApprovalDecision(jobToApprove.id, false)}
                className="mb-[9px] flex w-full items-center gap-[11px] rounded-xl border border-[#E0E3EB] bg-white px-[15px] py-[13px] text-left text-[#2B3142] transition-colors hover:border-[#28C0D0]"
              >
                <MailX className="h-[19px] w-[19px]" strokeWidth={2} />
                <div>
                  <div className="text-[13.5px] font-semibold" style={{ fontFamily: "Poppins,sans-serif" }}>Approve Silently</div>
                  <div className="text-[11.5px] opacity-70">No notifications sent</div>
                </div>
              </button>
              <button
                onClick={() => { setShowApprovalModal(false); setJobToApprove(null); }}
                className="w-full rounded-xl py-[11px] text-[13px] font-semibold text-[#8A90A2]"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Push Notification Modals */}
      <PushNotificationModals
        showPushModal={showPushModal}
        setShowPushModal={setShowPushModal}
        pushRegCount={pushRegCount}
        pushRegUsers={pushRegUsers}
        pushRegPage={pushRegPage}
        pushRegTotalPages={pushRegTotalPages}
        pushRegSearch={pushRegSearch}
        setPushRegSearch={setPushRegSearch}
        loadingPushReg={loadingPushReg}
        fetchPushRegUsers={fetchPushRegUsers}
        showSendPushModal={showSendPushModal}
        setShowSendPushModal={setShowSendPushModal}
        sendPushUser={sendPushUser}
        setSendPushUser={setSendPushUser}
        pushTitle={pushTitle}
        setPushTitle={setPushTitle}
        pushMessage={pushMessage}
        setPushMessage={setPushMessage}
        pushLinkUrl={pushLinkUrl}
        setPushLinkUrl={setPushLinkUrl}
        sendingPush={sendingPush}
        handleSendPush={handleSendPush}
      />
    </div>
  );
};

export default AdminDashboard;
